var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { SchemaLink } from 'apollo-link-schema';
import qs from 'qs';
import createCache from '../data/cache';
import report from '../errorReporting';
import { resolvers } from '../resolvers';
import typeDefs from './client-schema.gql';
import { logOperationTime } from './utils/queryLoggingMiddleware';
import { link as networkStatusLink } from '@jetshop/ui/Loading/NetworkStatusNotifier';
export const createClient = ({ apolloConfig: { token, shopid, enableGateway, graphQLURI, useGraphQLProxyServer }, schema, authHandler, previewHandler, req, channelHandler }) => {
    if (!token || !shopid) {
        throw new Error(`Missing ${!token && `token`} ${!shopid && `shopid`}`);
    }
    const authHeaders = authHandler ? authHandler.getAuthHeaders() : {};
    const previewHeaders = previewHandler
        ? previewHandler.getPreviewHeaders()
        : {};
    const createLocalGraphqlServerLink = () => __IN_SERVER__
        ? createHttpLink({
            uri: `http://localhost:${process.env.PORT || 3000}/graphql`,
            headers: {},
            fetch: require('node-fetch').default
        })
        : createHttpLink({
            uri: '/graphql',
            headers: {}
        });
    const createGatewayLink = () => __IN_SERVER__
        ? schema
            ? new SchemaLink({
                schema,
                context: {
                    headers: Object.assign(Object.assign(Object.assign({}, ((channelHandler === null || channelHandler === void 0 ? void 0 : channelHandler.getChannelHeaders()) || {})), authHeaders), previewHeaders)
                }
            })
            : createHttpLink({
                uri: `http://localhost:${process.env.PORT || 3000}/graphql`,
                headers: {},
                fetch: require('node-fetch').default
            })
        : createHttpLink({
            uri: '/graphql',
            headers: {}
        });
    const createDirectLink = () => {
        if (__IN_SERVER__) {
            return createHttpLink({
                uri: graphQLURI,
                headers: {
                    token: token,
                    shopid: shopid
                },
                fetch: require('node-fetch').default,
                fetchOptions: {
                    agent: graphQLURI.indexOf('https') === 0
                        ? new (require('https').Agent)({
                            keepAlive: true
                        })
                        : undefined
                }
            });
        }
        return createHttpLink({
            uri: graphQLURI,
            headers: {
                token: token,
                shopid: shopid
            },
            fetchOptions: {
                keepAlive: true
            }
        });
    };
    const apiLink = useGraphQLProxyServer
        ? createLocalGraphqlServerLink()
        : enableGateway
            ? createGatewayLink()
            : createDirectLink();
    const channelMiddleware = new ApolloLink((operation, forward) => {
        // Sets the current channel before each new HTTP request is made
        operation.setContext(({ headers = {} }) => ({
            headers: Object.assign(Object.assign({}, headers), ((channelHandler === null || channelHandler === void 0 ? void 0 : channelHandler.getChannelHeaders()) || {}))
        }));
        return forward ? forward(operation) : null;
    });
    const authMiddleware = authHandler &&
        new ApolloLink((operation, forward) => {
            // Sets the auth token when a new request is made
            authHandler.loadAuthToken();
            operation.setContext(({ headers = {} }) => ({
                headers: Object.assign(Object.assign({}, headers), authHandler.getAuthHeaders())
            }));
            return forward ? forward(operation) : null;
        });
    const previewMiddleware = previewHandler &&
        new ApolloLink((operation, forward) => {
            operation.setContext(({ headers = {} }) => ({
                headers: Object.assign(Object.assign({}, headers), previewHandler.getPreviewHeaders())
            }));
            return forward ? forward(operation) : null;
        });
    const errorMiddleware = onError((_a) => {
        var _b;
        var { graphQLErrors, networkError, operation } = _a, rest = __rest(_a, ["graphQLErrors", "networkError", "operation"]);
        if (graphQLErrors) {
            graphQLErrors.forEach((error) => {
                var _a;
                const code = (_a = error === null || error === void 0 ? void 0 : error.extensions) === null || _a === void 0 ? void 0 : _a.code;
                if (code === 'EmptyRoute') {
                    console.log(`[404 error at ${operation.variables.path}]`);
                    report(new Error(`MissingRoute: Attempted to access ${operation.variables.path}`));
                }
                else if (code === 'InvalidToken' || code === 'TokenExpired') {
                    authHandler.clearAuthToken();
                    if (typeof window !== 'undefined') {
                        window.location.href = '/';
                    }
                }
                else if (code === 'InvalidCreateCustomerInput' ||
                    code === 'UnableToLoginCustomer') {
                    // Don't report this to Sentry
                    console.log(error);
                }
                else {
                    console.log(error);
                    // Other graph errors will be logged to Sentry
                    report(new Error(`${error.message || code}`), Object.assign({ 
                        // Include all operation details (query etc)
                        operation,
                        code,
                        error,
                        graphQLErrors,
                        networkError }, rest));
                }
            });
        }
        // 503
        if (networkError) {
            const errors = (_b = networkError === null || networkError === void 0 ? void 0 : networkError.result) === null || _b === void 0 ? void 0 : _b.Errors;
            if (errors) {
                errors.forEach((error) => {
                    if ((error === null || error === void 0 ? void 0 : error.Message) === 'Token has expired') {
                        authHandler.clearAuthToken();
                        if (typeof window !== 'undefined') {
                            window.location.href = '/';
                        }
                    }
                });
            }
            console.log(`[Network error]: ${networkError}`);
            report(networkError);
        }
    });
    const links = [
        ...(__IN_SERVER__ ? [] : [networkStatusLink]),
        errorMiddleware,
        // Only add the middleware to the links array if the exist
        ...(channelMiddleware ? [channelMiddleware] : []),
        ...(authMiddleware ? [authMiddleware] : []),
        ...(previewMiddleware ? [previewMiddleware] : []),
        apiLink
    ];
    if (process.env.DEBUG_MODE === 'true') {
        links.unshift(logOperationTime);
    }
    if (enableGateway) {
        links.unshift(createPersistedQueryLink({ useGETForHashedQueries: true }));
    }
    const link = ApolloLink.from(links);
    const cache = createCache();
    const client = new ApolloClient({
        link,
        assumeImmutableResults: true,
        cache: __IN_SERVER__
            ? cache
            : cache.restore(window.__APOLLO_STATE__),
        ssrMode: !!__IN_SERVER__,
        resolvers: resolvers(qs.stringify(req === null || req === void 0 ? void 0 : req.query, { encode: true, encodeValuesOnly: true })),
        typeDefs
    });
    return { client };
};
//# sourceMappingURL=apollo.js.map